import { ColumnDef, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { TableShimmeringDiv } from 'src/tables/Table';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { numberRangeFilter } from '../filters/filterFunctions';

interface NumberColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get the number value from data */
  accessor: (data: TData) => number | null | undefined;
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Section type for precision settings */
  sectionType?: SectionType;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Include footer with aggregated totals */
  withFooter?: boolean;
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Use user-defined precision from column settings */
  useUserPrecision?: boolean;
  /** Function to generate className based on row data */
  className?: (data: TData) => string | undefined;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
  /** Custom cell component that completely overrides default cell rendering (allows hooks) */
  customCell?: React.ComponentType<{
    getValue: () => any;
    row: { original: TData };
    column: { id: string };
    table: Table<TData>;
  }>;
}

/**
 * Factory function to create number column definitions for tables.
 * Handles numeric values with user-defined precision formatting.
 *
 * @example
 * createNumberColumn({
 *   id: SalesTableColumnId.QuantitySold,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.QuantitySold],
 *   accessor: (data) => data?.sale?.qtySold,
 *   size: COLUMN_DEFAULT_SIZE_BASE,
 *   withFooter: true,
 *   useUserPrecision: true,
 * })
 */
export const createNumberColumn = <TData,>(
  options: NumberColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    sectionType = SectionType.Sales,
    align = 'right',
    withFooter = false,
    enableColumnFilter = true,
    filterFn,
    sortingFn,
    useUserPrecision = true,
    className,
    shouldRender,
    customCell: CustomCell,
  } = options;

  // Smart default: use numberRangeFilter if filtering is enabled and no custom filterFn
  const actualFilterFn =
    filterFn ?? (enableColumnFilter ? numberRangeFilter : undefined);

  return {
    id,
    enableColumnFilter,
    ...(actualFilterFn && { filterFn: actualFilterFn }),
    ...(sortingFn && { sortingFn }),
    size,
    minSize,
    maxSize,
    header: () => (
      <TableHeaderCellDiv align={'left'}>
        <Content id={headerContentId} />
      </TableHeaderCellDiv>
    ),
    accessorFn: accessor,
    meta: {
      columnFilterVariant: 'numberRange',
      align,
    },
    cell: CustomCell
      ? CustomCell
      : function Cell({
          getValue,
          row: { original },
          column: { id: columnId },
          table,
        }) {
          const value = getValue<number | null>();

          const { userDefinedPrecision } = useColumnUserSetting(
            columnId,
            table.options.meta?.sectionType ?? sectionType
          );

          if (!original) {
            return (
              <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
            );
          }

          if (shouldRender && !shouldRender(original, table)) {
            return null;
          }

          if (!value) {
            return <BaseCell />;
          }

          const cellClassName = className?.(original);

          return (
            <BaseCell align={!value ? 'left' : align}>
              {cellClassName ? (
                <div className={cellClassName}>
                  {useUserPrecision
                    ? applyNumberFormatting({
                        precision: userDefinedPrecision,
                        inputNumber: value,
                      })
                    : value}
                </div>
              ) : useUserPrecision ? (
                applyNumberFormatting({
                  precision: userDefinedPrecision,
                  inputNumber: value,
                })
              ) : (
                value
              )}
            </BaseCell>
          );
        },
    ...(withFooter && {
      footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
        const original = table.options.meta?.summaryData;

        const { userDefinedPrecision } = useColumnUserSetting(
          columnId,
          sectionType ?? table.options.meta?.sectionType ?? SectionType.Listings
        );

        if (!original) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const value = accessorFn?.(original, -1) as number | null;

        if (value == null) {
          return null;
        }

        return (
          <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
            {value != null && !isNaN(value)
              ? userDefinedPrecision
                ? applyNumberFormatting({
                    precision: userDefinedPrecision,
                    inputNumber: value,
                  })
                : value
              : null}
          </BaseCell>
        );
      },
    }),
  };
};
